/**
 * Status API Client
 * 用於更新模型 Marker 的狀態文字
 */

// 動態設定 API URL
const currentHost = window.location.hostname;
const currentProtocol = window.location.protocol;

let API_BASE_URL: string;
let WS_URL: string;

if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
  API_BASE_URL = 'http://localhost:3001/api';
  WS_URL = 'ws://localhost:3001';
} else {
  API_BASE_URL = `${currentProtocol}//${currentHost}/api`;
  WS_URL = `${currentProtocol === 'https:' ? 'wss:' : 'ws:'}//${currentHost}`;
}

export interface StatusUpdate {
  folderId: string;
  status: string;
}

export class StatusAPI {
  private static ws: WebSocket | null = null;
  private static reconnectTimer: number | null = null;
  private static reconnectAttempts = 0;
  private static MAX_RECONNECT_ATTEMPTS = 5;
  /**
   * 更新單個 Folder 的狀態
   */
  static async updateStatus(folderId: string, status: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/status/${folderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json();
      
      if (data.success) {
        // 更新 DOM 中的狀態文字
        this.updateMarkerDOM(folderId, status);
        return true;
      } else {
        console.error(`❌ Failed to update status for folder ${folderId}:`, data.error);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error updating status for folder ${folderId}:`, error);
      return false;
    }
  }

  /**
   * 批量更新多個 Folder 的狀態
   */
  static async updateBatchStatus(updates: StatusUpdate[]): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/status/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      const data = await response.json();
      
      if (data.success) {
        // 更新所有 DOM 中的狀態文字
        updates.forEach(({ folderId, status }) => {
          this.updateMarkerDOM(folderId, status);
        });
        return true;
      } else {
        console.error(`❌ Failed to batch update:`, data.error);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error batch updating:`, error);
      return false;
    }
  }

  /**
   * 獲取單個 Folder 的狀態
   */
  static async getStatus(folderId: string): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/status/${folderId}`);
      const data = await response.json();
      
      if (data.success) {
        return data.status;
      } else {
        console.error(`❌ Failed to get status for folder ${folderId}:`, data.error);
        return null;
      }
    } catch (error) {
      console.error(`❌ Error getting status for folder ${folderId}:`, error);
      return null;
    }
  }

  /**
   * 獲取所有 Folder 的狀態
   */
  static async getAllStatuses(): Promise<Array<{id: string, name: string, status: string}> | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/status`);
      const data = await response.json();
      
      if (data.success) {
        return data.folders;
      } else {
        console.error(`❌ Failed to get all statuses:`, data.error);
        return null;
      }
    } catch (error) {
      console.error(`❌ Error getting all statuses:`, error);
      return null;
    }
  }

  /**
   * 刪除 Folder 的狀態（恢復為 Normal）
   */
  static async deleteStatus(folderId: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/status/${folderId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      
      if (data.success) {
        // 更新 DOM 為 Normal
        this.updateMarkerDOM(folderId, 'Normal');
        return true;
      } else {
        console.error(`❌ Failed to delete status for folder ${folderId}:`, data.error);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error deleting status for folder ${folderId}:`, error);
      return false;
    }
  }

  /**
   * 更新 DOM 中的 Marker 狀態文字
   */
  private static updateMarkerDOM(folderId: string, status: string): void {
    // 檢測警告關鍵字（不區分大小寫）
    const alertKeywords = ['alert', 'warning', '警告', '告警', '異常', 'error', '錯誤', 'danger', '危險'];
    const isAlert = alertKeywords.some(keyword => 
      status.toLowerCase().includes(keyword.toLowerCase())
    );
    
    // 更新地圖上的 Marker（只選擇 .marker-status 元素）
    const statusElement = document.querySelector(`.marker-status[data-folder-id="${folderId}"]`) as HTMLElement;
    if (statusElement) {
      statusElement.textContent = status;
      
      // 獲取父 marker 元素
      const markerElement = statusElement.closest('.model-marker') as HTMLElement;
      
      if (markerElement) {
        if (isAlert) {
          // 警告樣式：紅底、白字、白色 neon light
          markerElement.style.background = 'rgba(255, 0, 0, 0.9)';
          markerElement.style.borderColor = 'white';
          markerElement.style.boxShadow = '0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.3)';
          
          // 設定所有子元素文字為白色
          const folderDiv = markerElement.querySelector('.marker-folder') as HTMLElement;
          const statusDiv = markerElement.querySelector('.marker-status') as HTMLElement;
          if (folderDiv) folderDiv.style.color = 'white';
          if (statusDiv) statusDiv.style.color = 'white';
        } else {
          // 恢復正常樣式（清空 inline styles，使用 CSS 原始樣式）
          markerElement.style.background = '';
          markerElement.style.borderColor = '';
          markerElement.style.boxShadow = '';
          
          // 恢復子元素文字顏色
          const folderDiv = markerElement.querySelector('.marker-folder') as HTMLElement;
          const statusDiv = markerElement.querySelector('.marker-status') as HTMLElement;
          if (folderDiv) folderDiv.style.color = '';
          if (statusDiv) statusDiv.style.color = '';
        }
      }
    } else {
      console.warn(`⚠️ Marker element not found for folder ${folderId}`);
    }
    
    // 更新 Panel 中的狀態
    const panelStatusElement = document.querySelector(`[data-panel-folder-id="${folderId}"]`) as HTMLElement;
    if (panelStatusElement) {
      panelStatusElement.textContent = status;
      
      if (isAlert) {
        panelStatusElement.classList.add('alert');
      } else {
        panelStatusElement.classList.remove('alert');
      }
    } else {
      console.warn(`⚠️ [StatusAPI] Panel element NOT found for "${folderId}"`);
    }
  }

  /**
   * 初始化時從 API 載入所有狀態，並註冊 Folders
   */
  static async initializeStatuses(): Promise<void> {
    // 1. 收集所有 Folder 名稱
    const folderNames = this.collectAllFolderNames();
    
    // 2. 註冊到 API Server
    if (folderNames.length > 0) {
      try {
        const response = await fetch(`${API_BASE_URL}/folders/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ folders: folderNames }),
        });
        
        await response.json();
      } catch (error) {
        console.warn('⚠️ Could not register folders to API:', error);
      }
    }
    
    // 3. 從 API 載入所有狀態
    const folders = await this.getAllStatuses();
    
    if (folders && folders.length > 0) {
      folders.forEach(folder => {
        this.updateMarkerDOM(folder.id, folder.status);
      });
    }
    
    // 4. 建立 WebSocket 連接以接收即時更新
    this.connectWebSocket();
  }

  /**
   * 建立 WebSocket 連接
   */
  private static connectWebSocket(): void {
    try {
      this.ws = new WebSocket(WS_URL);
      
      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'init') {
            // 初始化時收到所有 Folders
            data.folders.forEach((folder: any) => {
              this.updateMarkerDOM(folder.id, folder.status);
            });
          } else if (data.type === 'statusUpdate') {
            // 收到狀態更新
            this.updateMarkerDOM(data.folder.id, data.folder.status);
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };
      
      this.ws.onclose = () => {
        this.ws = null;
        this.attemptReconnect();
      };
      
      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
      };
    } catch (error) {
      console.error('❌ Failed to create WebSocket:', error);
      this.attemptReconnect();
    }
  }

  /**
   * 嘗試重新連接 WebSocket
   */
  private static attemptReconnect(): void {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      console.warn('⚠️ Max reconnection attempts reached. Please refresh the page.');
      return;
    }
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    this.reconnectAttempts++;
    
    this.reconnectTimer = window.setTimeout(() => {
      this.connectWebSocket();
    }, delay);
  }

  /**
   * 關閉 WebSocket 連接
   */
  static disconnectWebSocket(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 收集頁面上所有的 Folder 資訊（ID 和 Name）
   */
  private static collectAllFolderNames(): Array<{id: string, name: string}> {
    const folderMap = new Map<string, string>();
    
    // 只選擇地圖上的 marker，不包括 panel 中的元素
    const markers = document.querySelectorAll('.marker-status[data-folder-id]');
    markers.forEach((marker) => {
      const folderId = marker.getAttribute('data-folder-id');
      const folderNameEl = marker.parentElement?.querySelector('.marker-folder');
      const folderName = folderNameEl?.textContent || folderId;
      
      if (folderId) {
        folderMap.set(folderId, folderName || folderId);
      }
    });
    
    return Array.from(folderMap.entries()).map(([id, name]) => ({ id, name }));
  }

  /**
   * 列出所有可用的 Folder IDs 和名稱
   */
  static listAllFolders(): void {
    // 從 DOM 中獲取所有 marker（只從地圖上的 marker）
    const markers = document.querySelectorAll('.marker-status[data-folder-id]');
    const folderMap = new Map<string, string>();
    
    markers.forEach((marker) => {
      const folderId = marker.getAttribute('data-folder-id');
      const folderNameEl = marker.parentElement?.querySelector('.marker-folder');
      const folderName = folderNameEl?.textContent || 'Unknown';
      
      if (folderId && !folderMap.has(folderId)) {
        folderMap.set(folderId, folderName);
      }
    });
    
    console.log('📁 Available Folders:');
    console.log('═══════════════════════════════════════════');
    
    if (folderMap.size === 0) {
      console.log('⚠️ No folders found. Make sure models are loaded.');
      return;
    }
    
    folderMap.forEach((name, id) => {
      console.log(`📂 ${name}`);
      console.log(`   ID: ${id}`);
      console.log(`   Usage: await StatusAPI.updateStatus('${id}', '你的狀態')`);
      console.log('───────────────────────────────────────────');
    });
    
    console.log(`\n✅ Total: ${folderMap.size} folder(s)`);
    
    // 也從 localStorage 讀取
    try {
      const storedFolders = localStorage.getItem('echov-folders');
      if (storedFolders) {
        const folders = JSON.parse(storedFolders);
        console.log('\n📦 From localStorage (echov-folders):');
        folders.forEach((folder: any) => {
          console.log(`   ${folder.name} → ${folder.id}`);
        });
      }
    } catch (error) {
      console.warn('⚠️ Could not read from localStorage');
    }
  }

  /**
   * 列出所有 Marker 及其當前狀態
   */
  static listAllMarkers(): void {
    const markers = document.querySelectorAll('.marker-status[data-folder-id]');
    
    console.log('🗺️ All Markers Status:');
    console.log('═══════════════════════════════════════════');
    
    markers.forEach((marker, index) => {
      const folderId = marker.getAttribute('data-folder-id');
      const status = marker.textContent;
      const folderNameEl = marker.parentElement?.querySelector('.marker-folder');
      const folderName = folderNameEl?.textContent || 'Unknown';
      
      console.log(`${index + 1}. ${folderName}`);
      console.log(`   Folder ID: ${folderId}`);
      console.log(`   Status: ${status}`);
      console.log('───────────────────────────────────────────');
    });
    
    console.log(`\n✅ Total: ${markers.length} marker(s)`);
  }
}

// 暴露到全域供除錯使用
if (typeof window !== 'undefined') {
  (window as any).StatusAPI = StatusAPI;
}
